import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

export type SectionCardVariant = 'large' | 'medium' | 'small' | 'feature' | 'feature-second' | 'info' | 'banner';

/**
 * Reusable "section card" — the rounded, padded surface (optional gradient/solid
 * background) with a title, optional subtitle, and projected content used across the
 * landing-style pages (Score, Sanctuary, Landing).
 *
 * Variants set sensible defaults; everything (background, padding, radius, gap, shadow,
 * title/subtitle colour + size) is overridable per-instance via inputs, or from the
 * parent's stylesheet by setting the `--sc-*` custom properties on `app-section-card`.
 *
 * Variant → reference shape:
 *   large   — section-buffer / take-a-moment   (big section heading + content)
 *   medium  — section-super-stars / your-tools (solid sub-section card)
 *   small   — community-corner / learn         (compact card; solid or gradient)
 *   feature — section-banner / take-a-minute    (image + title + text + action,
 *             responsive grid that re-flows between desktop and mobile)
 *   info    — section-track / mindfulness-grounding (dashed-border "info card":
 *             translucent fill, heading + body + pill; `borderColor` sets the dash
 *             colour, and `cardFromDesktop` + `hideHeadingOnMobile` reproduce the
 *             section-track collapse to a bare pill on mobile)
 *   banner  — today's-perspective style: single-row horizontal flex, image left,
 *             heading + subtitle right, no action slot
 *
 * Content projection:
 *   - default slot → the custom content below the heading (standard variants) or extra
 *     body content (feature variant)
 *   - `[sectionImage]` → the illustration/image (feature variant only)
 *   - `[sectionTitleImage]` → small icon/image sat beside the title, on the same row;
 *     the subtitle still spans the full width underneath both
 *   - `[sectionHeaderAction]` → action pinned to the end of the title row (e.g. "View all")
 *   - `[sectionAction]` → the button/CTA/player (feature variant only)
 */
@Component({
  selector: 'app-section-card',
  templateUrl: './section-card.component.html',
  styleUrls: ['./section-card.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.variant-large]': "variant === 'large'",
    '[class.variant-medium]': "variant === 'medium'",
    '[class.variant-small]': "variant === 'small'",
    // `initial` (not null) when no background is set: custom properties inherit, so a
    // bare null would let an ancestor section-card's --sc-bg-user leak in and override this
    // card's variant default (e.g. an info card nested inside a large gradient card). Setting
    // it to the guaranteed-invalid `initial` blocks inheritance and makes var(--sc-bg-user, …)
    // fall back to the variant default.
    '[style.--sc-bg-user]': "background || 'initial'",
  },
})
export class SectionCardComponent {
  /** Layout/size preset. Drives the `variant-<x>` host class and its default tokens. */
  @Input() variant: SectionCardVariant = 'medium';

  /** Heading text. Omitted from the DOM when empty. */
  @Input() title = '';

  /** Sub-heading text below the title. Omitted from the DOM when empty. */
  @Input() subtitle = '';

  /**
   * When true, the card chrome (background, shadow, radius, padding) is suppressed on
   * mobile and only appears at `lg+`. Reproduces the section-buffer / take-a-moment
   * behaviour where the page wrapper supplies the gradient on mobile.
   */
  @Input() cardFromDesktop = false;

  /** Side the illustration sits on (feature variant only). */
  @Input() imageSide: 'left' | 'right' = 'left';

  /** CSS background (gradient or colour). Overrides the variant default when set. */
  @Input() background?: string;

  /** CSS padding override (e.g. `var(--col)` or `16px 16px 0`). */
  @Input() padding?: string;

  /** CSS border-radius override. */
  @Input() radius?: string;

  /** CSS gap override between heading and content (standard variants). */
  @Input() gap?: string;

  /** CSS box-shadow override. */
  @Input() boxShadow?: string;

  /** Dashed border colour for the `info` variant (any CSS colour). */
  @Input() borderColor?: string;

  /**
   * Hide the heading (title + subtitle) on mobile, leaving only the projected content.
   * Combine with `cardFromDesktop` to reproduce section-track's "bare pill on mobile,
   * full dashed card at lg+" behaviour.
   */
  @Input() hideHeadingOnMobile = false;

  /** Title colour override. */
  @Input() titleColor?: string;

  /** Subtitle colour override. */
  @Input() subtitleColor?: string;

  /** Title font-size override. */
  @Input() titleFontSize?: string;

  /** Subtitle font-size override. */
  @Input() subtitleFontSize?: string;

  /**
   * Show a trailing forward arrow CTA pinned to the end (learn-card style). When set the
   * card lays out horizontally — text block on the left, arrow on the right. Intended for
   * the `small` variant.
   */
  @Input() showArrow = false;

  /** Icon used for the trailing arrow. */
  @Input() arrowIconSrc = 'assets/icons/icon-forward-with-circle-evergreen.svg';

  /** Optional router link applied to the trailing arrow. */
  @Input() arrowRouterLink?: string | (string | number)[];

  /** Accessible label for the icon-only arrow CTA. */
  @Input() arrowAriaLabel = '';

  /** Emitted when the trailing arrow is clicked. */
  @Output() arrowClick = new EventEmitter<void>();

  /**
   * When true, lays the card out as a horizontal row at `lg+` (text block left, projected
   * content right). Useful for compact cards that need a side-by-side title + action layout
   * on wider screens (e.g. community corner).
   */
  @Input() displayRowOnDesktop = false;

  /** Global typography class for the title — large uses the top-level section heading. */
  get titleClass(): string {
    return this.variant === 'large' ? 'section-heading' : 'subsection-heading';
  }

  /** Global typography class for the subtitle. */
  get subtitleClass(): string {
    return this.variant === 'large' ? 'section-subheading' : 'subsection-subheading';
  }
}
